import React, { useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, Polyline, Marker, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";


const pinIcon = new L.Icon({
    iconUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 10'%3E%3Ccircle cx='5' cy='5' r='4' fill='%231976d2' stroke='white' stroke-width='1.2'/%3E%3C/svg%3E",    
    iconSize: [11, 11],         
    iconAnchor: [5.5, 5.5],   
});

const MapComponent = ({ flights, selectedSector, resizeTrigger }) => {
    const mapRef = useRef(null);
    const params = useSelector((state) => state.params);

    const DEFAULT_ZOOM = useMemo(
        () => Number(params?.find(item => item.param === "DEFAULT_ZOOM")?.value) || 5,
        [params],
    );

    const DEFAULT_CENTER = useMemo(() => {
        const defaultValue = params?.find(item => item.param === "MAP_CENTER_DEFAULT")?.value;
        if (!defaultValue) return [21.0, 105.0];

        const [lat, lng] = defaultValue.split(",").map(Number);

        return [lat || 21.0, lng || 105.0];
    }, [params]);

    const visibleFlights = useMemo(
        () => selectedSector
            ? flights.filter(f => f.sector === selectedSector)
            : flights,
        [flights, selectedSector]
    );

    // Deduplicate markers: many flights share the same airport → render each airport once
    const uniqueAirports = useMemo(() => {
        const map = new Map();
        for (const s of visibleFlights) {
            for (const f of s.flights || []) {
                if (f.dep && f.depCoordinates && !map.has(f.dep))
                    map.set(f.dep, { code: f.dep, coords: f.depCoordinates });
                if (f.arr && f.arrCoordinates && !map.has(f.arr))
                    map.set(f.arr, { code: f.arr, coords: f.arrCoordinates });
            }
        }
        return [...map.values()];
    }, [visibleFlights]);
    
    /** AUTO RESIZE */
    const autoCenter = useCallback(() => {
        if (!mapRef.current) return;

        const points = visibleFlights
            .flatMap(s => s?.flights || [])
            .flatMap(f => [f?.depCoordinates, f?.arrCoordinates])
            .filter(Boolean);

        if (points.length > 0) {
            mapRef.current.fitBounds(L.latLngBounds(points), {
                padding: [50, 50],
                animate: true,
                duration: 0.5, 
            });
        } else {
            mapRef.current.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
        }
    }, [DEFAULT_CENTER, DEFAULT_ZOOM, visibleFlights]);
    
    useLayoutEffect(() => {
        if (!mapRef.current) return;
        
        mapRef.current.invalidateSize();

        const handleResize = () => {
            if (mapRef.current) {
                mapRef.current.invalidateSize();
                autoCenter();
            }
        };

        const animationFrame = requestAnimationFrame(handleResize);
        const timeOut = setTimeout(handleResize, 1000);

        return () => {
            cancelAnimationFrame(animationFrame);
            clearTimeout(timeOut);
        };
    }, [resizeTrigger, autoCenter]); 


    return (
        <MapContainer             
            center={DEFAULT_CENTER} 
            zoom={DEFAULT_ZOOM} 
            style={{ height: "100%", width: "100%", zIndex: 0 }}
            whenReady={(mapInstance) => {
                mapRef.current = mapInstance.target;
            }}
        >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

            {/* Polyline */}
            {visibleFlights.flatMap((s, i) =>
                s.flights.map((f, j) =>
                    f.depCoordinates && f.arrCoordinates && (
                        <Polyline
                            key={`${i}-${j}`}
                            positions={[f.depCoordinates, f.arrCoordinates]}
                            pathOptions={{ color: "#1976d2", weight: 2 }}
                        />
                    )
                )
            )}

            {/* Markers — one per unique airport, not one per flight */}
            {uniqueAirports.map(({ code, coords }) => (
                <Marker key={code} position={coords} icon={pinIcon}>
                    <Tooltip>{code}</Tooltip>
                </Marker>
            ))}
        </MapContainer>
    );
};

const areEqual = (prev, next) => {
    return (
        prev.selectedSector === next.selectedSector &&
        prev.resizeTrigger === next.resizeTrigger &&
        prev.flights === next.flights
    );
};

export default React.memo(MapComponent, areEqual);
